import React from "react";
import { Badge, Button, Card, Collapse } from "react-bootstrap";
import { Check, ChevronRight, X } from "react-bootstrap-icons";
import MultiPicker from "../MultiPicker/MultiPicker";
import { ModeGrantModel } from "../../models/AccountPermissionModel";
import { CategoryJsonModel, LicenseJsonModel, ModeJsonModel } from "../../models/BlockTransportSystemJsonModel";
import { colorFromString } from "../../utils/ColorFromString";

interface IProps {
  mode: ModeJsonModel;
  grant: ModeGrantModel;
  categories: CategoryJsonModel[];
  deletedCategories: CategoryJsonModel[];
  licenses: LicenseJsonModel[];
  isOpen: boolean;
  isAccentCards?: boolean;
  onToggleOpen: () => void;
  update: (fn: (grant: ModeGrantModel) => ModeGrantModel) => void;
  onRemove: () => void;
}

// 부여된 항목 칩 (클릭 시 제거). bgColor 지정 시 Bootstrap 변형(tone) 대신 커스텀 색.
const RemovableChip = ({ label, tone, bgColor, onClick }: { label: React.ReactNode; tone?: string; bgColor?: string; onClick: () => void }) => (
  <Badge
    bg={bgColor ? "" : tone}
    role="button"
    className="d-inline-flex align-items-center gap-1"
    style={{ cursor: "pointer", padding: "7px 11px", fontWeight: 500, ...(bgColor ? { backgroundColor: bgColor } : {}) }}
    onClick={onClick}
    title="클릭: 제거"
  >
    <Check size={12} />{label}<X size={12} />
  </Badge>
);

// 보라색 — 카테고리 칩/추가버튼 색상
const CAT_PURPLE = "#8079DB";
const CAT_CORAL = "#f26666";
const CHIP_VISIBLE_LIMIT = 8; // 이보다 많으면 "+n개 더보기"로 접기

// ── 카테고리 블록 ─────────────────────────────────────────────
const CategoryBlock = ({ grant, update, categories, deletedCategories }: Pick<IProps, "grant" | "update" | "categories" | "deletedCategories">) => {
  const granted = grant.categories;
  // 후보 풀 = 전체 카테고리 카탈로그. 모드 노출에 묶지 않는다(부여가 노출을 초과해 저장돼 있을 수 있어서).
  const catalog = categories;
  const [isShowAll, setIsShowAll] = React.useState(false);

  const toggle = (cid: string) =>
    update((grant) => ({
      ...grant,
      categories: grant.categories.includes(cid) ? grant.categories.filter((categoryId) => categoryId !== cid) : [...new Set([...grant.categories, cid])],
    }));
  const bulk = (ids: string[], isAdd: boolean) =>
    update((grant) => ({
      ...grant,
      categories: isAdd ? [...new Set([...grant.categories, ...ids])] : grant.categories.filter((categoryId) => !ids.includes(categoryId)),
    }));

  const visible = isShowAll ? granted : granted.slice(0, CHIP_VISIBLE_LIMIT);
  const hiddenCount = granted.length - visible.length;

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center mb-2 gap-2">
        <span className="fw-semibold small">카테고리</span>
        <span className="text-muted small ms-2">{granted.length}개 추가됨 / 전체 {catalog.length}</span>
        <span className="flex-grow-1" />
        {granted.length > 0 && (
          <Button variant="link" size="sm" className="text-muted p-0 text-decoration-none small me-3" onClick={() => update((grant) => ({ ...grant, categories: [] }))}>
            전체 비우기
          </Button>
        )}
        <MultiPicker
          label="카테고리 추가"
          color={CAT_PURPLE}
          items={catalog}
          selected={granted.filter(i => categories.some(c => c.id === i))}
          onToggle={toggle}
          onBulk={bulk}
          getId={(category) => category.id}
          getName={(category) => category.name}
        />
        {deletedCategories &&
        <MultiPicker
          label="카테고리 추가(삭제됨)"
          color={CAT_CORAL}
          items={deletedCategories}
          selected={granted.filter(i => deletedCategories.some(c => c.id === i))}
          onToggle={toggle}
          onBulk={bulk}
          getId={(category) => category.id}
          getName={(category) => category.name}
        />}
      </div>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {granted.length === 0 && <span className="text-muted small">‘카테고리 추가’에서 검색해 카테고리를 담으세요</span>}
        {visible.map((cid) => {
          let categoryName = "";
          let categoryChipColor = CAT_PURPLE;
          const category = categories.find((candidate) => candidate.id === cid);
          if (category){
            categoryName = category?.name ?? cid;
            categoryChipColor = CAT_PURPLE;
          } else {
            const deletedCategory = deletedCategories?.find((cat => cat.id === cid))
            if (deletedCategory){
              categoryName = deletedCategory?.name ?? cid;
              categoryChipColor = CAT_CORAL;
            }
          }

          return <RemovableChip key={cid} bgColor={categoryChipColor} onClick={() => toggle(cid)} label={categoryName} />;
        })}
        {hiddenCount > 0 && (
          <Button variant="link" size="sm" className="p-0 text-decoration-none small" onClick={() => setIsShowAll(true)}>
            +{hiddenCount}개 더보기
          </Button>
        )}
        {isShowAll && granted.length > CHIP_VISIBLE_LIMIT && (
          <Button variant="link" size="sm" className="text-muted p-0 text-decoration-none small" onClick={() => setIsShowAll(false)}>
            접기
          </Button>
        )}
      </div>
    </div>
  );
};

// ── 라이선스 블록 ─────────────────────────────────────────────
const LicenseBlock = ({ grant, update, licenses }: Pick<IProps, "grant" | "update" | "licenses">) => {
  // 후보 풀 = 전체 라이선스 카탈로그 (모드 노출에 라이선스가 비어 있어도 부여 가능하도록).
  const all = licenses;
  const selected = grant.licenses;
  const [isShowAll, setIsShowAll] = React.useState(false);

  const toggle = (id: string) =>
    update((grant) => ({
      ...grant,
      licenses: grant.licenses.includes(id) ? grant.licenses.filter((licenseId) => licenseId !== id) : [...new Set([...grant.licenses, id])],
    }));
  const bulk = (ids: string[], isAdd: boolean) =>
    update((grant) => ({
      ...grant,
      licenses: isAdd ? [...new Set([...grant.licenses, ...ids])] : grant.licenses.filter((licenseId) => !ids.includes(licenseId)),
    }));

  const visible = isShowAll ? selected : selected.slice(0, CHIP_VISIBLE_LIMIT);
  const hiddenCount = selected.length - visible.length;

  return (
    <div>
      <div className="d-flex align-items-center mb-2">
        <span className="fw-semibold small">라이선스</span>
        <span className="text-muted small ms-2">{selected.length} / {all.length} 선택</span>
        <span className="flex-grow-1" />
        <Button variant="link" size="sm" className="p-0 text-decoration-none small me-2" onClick={() => update((grant) => ({ ...grant, licenses: all.map((license) => license.id) }))}>
          전체 선택
        </Button>
        <Button variant="link" size="sm" className="text-muted p-0 text-decoration-none small me-3" onClick={() => update((grant) => ({ ...grant, licenses: [] }))}>
          해제
        </Button>
        <MultiPicker
          label="라이선스 추가"
          variant="success"
          items={all}
          selected={selected}
          onToggle={toggle}
          onBulk={bulk}
          getId={(license) => license.id}
          getName={(license) => license.name}
        />
      </div>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {selected.length === 0 && <span className="text-muted small">‘라이선스 추가’에서 검색해 선택하세요</span>}
        {visible.map((id) => {
          const license = licenses.find((candidate) => candidate.id === id);
          return <RemovableChip key={id} tone="success" onClick={() => toggle(id)} label={license ? license.name : id} />;
        })}
        {hiddenCount > 0 && (
          <Button variant="link" size="sm" className="p-0 text-decoration-none small" onClick={() => setIsShowAll(true)}>
            +{hiddenCount}개 더보기
          </Button>
        )}
        {isShowAll && selected.length > CHIP_VISIBLE_LIMIT && (
          <Button variant="link" size="sm" className="text-muted p-0 text-decoration-none small" onClick={() => setIsShowAll(false)}>
            접기
          </Button>
        )}
      </div>
    </div>
  );
};

// ── 모드 카드 ─────────────────────────────────────────────────
const AccountModeCard = ({ mode, grant, categories, deletedCategories, licenses, isOpen, isAccentCards, onToggleOpen, update, onRemove }: IProps) => (
  <Card className="mb-2" style={isAccentCards ? { borderLeft: `3px solid ${colorFromString(mode.id ?? mode.name)}` } : undefined}>
    <Card.Header
      className="bg-white d-flex align-items-center gap-2"
      style={{ cursor: "pointer" }}
      onClick={onToggleOpen}
    >
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: colorFromString(mode.id ?? mode.name), flexShrink: 0 }} />
      <span className="fw-semibold">{mode.name}</span>
      <Badge bg="" style={{ backgroundColor: CAT_PURPLE }}>카테고리 {grant.categories.filter(i => categories.some(c => c.id === i)).length}</Badge>
      <Badge bg="" style={{ backgroundColor: CAT_CORAL }}>카테고리 {grant.categories.filter(i => deletedCategories.some(c => c.id === i)).length}</Badge>
      <Badge bg="success">라이선스 {grant.licenses.length}</Badge>
      <span className="flex-grow-1" />
      <Button
        variant="outline-secondary"
        size="sm"
        className="d-flex align-items-center gap-1"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
      >
        <X size={14} />제거
      </Button>
      <ChevronRight size={18} style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }} />
    </Card.Header>
    <Collapse in={isOpen}>
      <div>
        <Card.Body className="bg-light">
          <CategoryBlock grant={grant} update={update} categories={categories} deletedCategories={deletedCategories} />
          <LicenseBlock grant={grant} update={update} licenses={licenses} />
        </Card.Body>
      </div>
    </Collapse>
  </Card>
);

export default AccountModeCard;
